import React, {
  useEffect,
  useMemo,
  useCallback,
  useReducer,
  useRef,
  forwardRef,
  useImperativeHandle,
} from "react";
import type { ReactElement, ComponentProps } from "react";

import { Input, Tooltip, FormControl } from "native-base";
import type { Text } from "native-base";

import { formatPercentage, isNumeric } from "pianofunclub-shared/utils/converters";
import { useDebounceState } from "pianofunclub-shared/utils/hooks";
import { percentageMask, percentageFinishEditMask } from "pianofunclub-shared/utils/inputs";
import { createInputReducer } from "pianofunclub-shared/utils/reducers";

type InputProps = ComponentProps<typeof Input>;
type TextProps = ComponentProps<typeof Text>;

export type PercentageInputRef = {
  clear: () => void;
  focus: () => void;
  setValue: (text: string, isValid: boolean) => void;
} | null;

interface Props extends InputProps {
  autoFocus?: boolean;
  centerLabel?: boolean;
  floatingErrorMessage?: string;
  floatingErrorOffset?: number;
  id: string;
  initiallyValid?: boolean;
  initialValue?: number | null;
  invalidIndicator?: boolean;
  label?: string;
  labelStyle?: TextProps;
  onFinishEditing?: (
    inputIdentifier: string | number,
    inputValue?: number,
    isValid?: boolean
  ) => void | string;
  onFocusInput?: (inputIdentifier: string | number, inputValue?: string, isValid?: boolean) => void;
  onInputChange?: (
    inputIdentifier: string | number,
    inputValue?: string,
    isValid?: boolean
  ) => void;
  onSubmit?: (id?: string | number, inputValue?: string, isValid?: boolean) => void;
  required?: boolean;
}

const PercentageInput = forwardRef((props: Props, ref): ReactElement => {
  const {
    autoFocus,
    centerLabel,
    floatingErrorMessage,
    floatingErrorOffset,
    id,
    initialValue,
    onFinishEditing,
    onFocusInput,
    onInputChange,
    onSubmit,
    ...rest
  } = props;

  const initialPercentage = useMemo(() => {
    // for duration inputs (initialValue in seconds)
    return formatPercentage(initialValue);
  }, [initialValue]);

  const initialRender = useRef(true);

  const inputReducer = createInputReducer();
  const [inputState, dispatch] = useReducer(inputReducer, {
    focussed: autoFocus ? true : false,
    hasBeenBlurred: false,
    isValid: props?.initiallyValid ?? false,
    touched: false,
    value: initialPercentage,
  });

  const {
    debounce,
    setDebouncedState: setDebouncedPercentage,
    state: debouncedPercentage,
  } = useDebounceState(inputState.value, 250);

  const inputRef = useRef<PercentageInputRef>(null);
  useImperativeHandle(ref, () => ({
    clear: () => {
      inputRef.current?.clear();
      dispatch({
        isValid: !props.required,
        type: "INPUT_CHANGE",
        value: "",
      });
    },
    focus: () => {
      inputRef.current?.focus();
    },
    setValue: (text: string, isValid: boolean) => {
      dispatch({
        isValid: isValid,
        type: "INPUT_CHANGE_NO_FOCUS",
        value: text,
      });
    },
  }));

  useEffect(() => {
    if (inputState.touched && onInputChange) {
      onInputChange(id, inputState.value, inputState.isValid);
    }
  }, [inputState, onInputChange, id]);

  useEffect((): void => {
    if (initialRender.current) {
      initialRender.current = false;
      return;
    }

    dispatch({
      isValid: true,
      type: "INPUT_CHANGE_NO_FOCUS",
      value: debouncedPercentage,
    });
  }, [debouncedPercentage]);

  useEffect((): (() => void) => {
    setDebouncedPercentage(inputState.value);

    return () => {
      debounce.cancel();
    };
  }, [debounce, inputState.value, setDebouncedPercentage]);

  const textChangeHandler = useCallback((text: string) => {
    dispatch({
      isValid: true,
      type: "INPUT_CHANGE",
      value: percentageMask(text),
    });
  }, []);

  const onFocusHandler = useCallback(() => {
    dispatch({ type: "INPUT_FOCUS" });
    onFocusInput?.(id, inputState.value, inputState.isValid);
  }, [id, inputState.isValid, inputState.value, onFocusInput]);

  const lostFocusHandler = useCallback(() => {
    dispatch({
      isValid: inputState.isValid,
      type: "INPUT_CHANGE",
      value: percentageFinishEditMask(inputState.value),
    });
    const trimmedInputValue = inputState.value.replace("%", "");
    const parsedInputValue = isNumeric(trimmedInputValue)
      ? parseFloat(trimmedInputValue)
      : undefined;
    const newValue = onFinishEditing?.(id, parsedInputValue, inputState.isValid);
    if (newValue) {
      dispatch({
        isValid: true,
        type: "INPUT_CHANGE",
        value: newValue,
      });
    }
    dispatch({ type: "INPUT_BLUR" });
  }, [id, inputState.isValid, inputState.value, onFinishEditing]);

  const onSubmitHandler = useCallback(() => {
    dispatch({ type: "INPUT_BLUR" });
    onSubmit?.(id, inputState.value, inputState.isValid);
  }, [id, inputState.isValid, inputState.value, onSubmit]);

  return (
    <FormControl
      isInvalid={(props?.invalidIndicator ?? false) && !inputState.isValid && inputState.touched}
      isRequired={props?.required ?? false}
    >
      {props?.label ? (
        <FormControl.Label
          _text={{
            color: "surface.100",
            fontFamily: "Poppins-SemiBold",
            fontSize: "md",
            pl: centerLabel ? "0" : "1",
            ...props.labelStyle,
          }}
          justifyContent={centerLabel ? "center" : undefined}
        >
          {props.label}
        </FormControl.Label>
      ) : null}
      <Tooltip
        _text={{ color: "warmGray.500" }}
        bg="surface.900"
        isOpen={
          floatingErrorMessage
            ? props?.invalidIndicator && inputState.focussed && !inputState.isValid
            : false
        }
        label={floatingErrorMessage ?? ""}
        mt={floatingErrorOffset ?? 0}
        opacity={0.7}
        placement="bottom"
        pointerEvents="none"
        px="1"
        py="0.5"
      >
        <Input
          ref={inputRef}
          _disabled={{
            bg: "transparent",
            borderWidth: 0,
            opacity: 1,
            py: 0,
          }}
          _focus={{
            bg: "surface.100",
            borderColor: "surface.300",
          }}
          _hover={{
            bg: "surface.100",
            borderColor: "surface.400",
          }}
          autoFocus={autoFocus}
          bg="surface.100"
          borderColor="surface.200"
          color="surface.900"
          keyboardType="number-pad"
          onBlur={lostFocusHandler}
          onChangeText={textChangeHandler}
          onFocus={onFocusHandler}
          onSubmitEditing={onSubmitHandler}
          placeholderTextColor="muted.400"
          returnKeyType="done"
          selectionColor="secondary.300"
          selectTextOnFocus
          value={inputState.value}
          {...rest}
        />
      </Tooltip>
    </FormControl>
  );
});

export default React.memo(PercentageInput);
